import { useReactiveVar } from '@apollo/client'
import { Button } from '@material-ui/core'
import React, { useState } from 'react'
import { Plus } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { projectVar } from '../../apollo/reactive-vars'
import { useBuildingEnergyModels } from '../../building-energy-model/use-building-energy-models'
import { Section } from '../common/Section'
import { BuildingEnergyModelsList } from './BuildingEnergyModelsList'
import { BuildingEnergyModelsDialog } from './dialog/BuildingEnergyModelsDialog'
import { FormValues } from './dialog/form-values'
import { useSchedules } from '../../schedule/use-schedules'
import {
  defaultValuesForAlways as scheduleDefaultAlways,
  defaultValuesForNever as scheduleDefaultNever,
  mapFormValuesToInput as mapScheduleValuesToInput,
} from './schedule/form-values'
import { LoadingDialog } from '../common/LoadingDialog'

export function BuildingEnergyModelsPage(): React.ReactElement {
  const project = useReactiveVar(projectVar)
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { createBuildingEnergyModel, isCreateLoading: isBemCreateLoading } =
    useBuildingEnergyModels()
  const { createSchedule, isCreateLoading: isScheduleCreateLoading } =
    useSchedules()

  if (!project) {
    return <></>
  }

  const onDialogClose = () => setOpen(false)

  const onCreateStart = () => setOpen(true)

  const onCreate = async ({ name, type, area }: FormValues) => {
    const input = {
      name,
      type,
      area: Number(area),
    }
    const energyModel = await createBuildingEnergyModel(project, input)

    if (energyModel) {
      // Add default schedules to energy model when creating new
      const alwaysInput = mapScheduleValuesToInput(scheduleDefaultAlways)
      await createSchedule(energyModel, alwaysInput)

      const neverInput = mapScheduleValuesToInput(scheduleDefaultNever)
      await createSchedule(energyModel, neverInput)
    }

    setOpen(false)
  }

  return (
    <Section
      title={t('buildingEnergyModels.header')}
      addon={
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<Plus />}
          onClick={onCreateStart}
        >
          {t('buildingEnergyModels.createNew')}
        </Button>
      }
    >
      <BuildingEnergyModelsList
        buildingEnergyModels={project.buildingEnergyModels}
      />
      {open && (
        <BuildingEnergyModelsDialog
          isOpen={open}
          isCreate
          onClose={onDialogClose}
          onSubmit={onCreate}
        />
      )}
      <LoadingDialog
        isLoading={isBemCreateLoading || isScheduleCreateLoading}
      />
    </Section>
  )
}
